import { List, Map } from 'immutable';

import { getFaireShipping, makeValue, shapeAboutValue, shapeEbayDetailsForApp, shapeEbayShippingAttributeForApp, shapeFaireDistanceUnitForApp, shapeFaireMassUnitForApp, shapeFairePricesForApp, shapeFaireShippingAttributeForApp, shapeFilesForApp, shapeKeyDate, shapeKeywordsForApp, shapePersonalizationForApp, shapeTaxonomyAttributesForApp, shapeUnixDate } from './shapeAttributes';
import { updateItemSpecificsForCategory, updateProductDetailsForCategory } from './taxonomy';
import { getOversetValue, isPricingInternational } from './checkAttribute';
import { getInventoryFromVariations } from '../inventory';
import { shapeVariationsForApp } from '../variations/shapeForApp';
import { photosFromImagesById } from '../photos';
import { updateListingScore } from './score';
import { toFixed, toString } from '../number';
import { videosFromProduct } from '../videos';
import { setProductErrors } from './errors';
import { tagsFromArray } from '../tags';
import { isVelaStatus } from '../listings/statuses';
import { shapeId } from '../listings/listings';
import { get } from '../iterable/get';

import { AMOUNT, OVERSET, REQUIRED, SIZE_UNIT, VALUE } from '../../constants/product';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { EMPTY, VELA } from '../../constants';
import { ATTRIBUTE } from '../../constants/attributes';
import { FEATURE } from '../../constants/features';
import { STATUS } from '../../constants/listings';
import { FORM } from '../../constants/forms';

function getNoProductError({ productId }) {
  return productId
    ? `No prouct details for product "${productId}" in API response`
    : 'No prouct details in API response';
}

export function getShopIdMatchError({ productId, productShopId, requestedShopId }) {
  return productId
    ? `Shop ID "${productShopId}" of requested product "${productId}" does not match requested shop ID "${requestedShopId}"`
    : `Shop ID "${productShopId}" of requested product does not match requested shop ID "${requestedShopId}"`;
}

export function shapeProductForApp({
  asProfile,
  channel,
  data,
  productId: id,
  shopData,
  shopId,
  taxonomyData,
  titleFormatPreference,
  userFeatures,
}) {
  const { imagesById = {}, products = [], productsById = {}, videos = {}} = data;
  const productId = shapeId(id || products[0]);
  const product = productsById[productId];

  if (!product) {
    throw new Error(getNoProductError({ productId }));
  }

  if (shapeId(product.shop_id) !== shopId) {
    throw new Error(getShopIdMatchError({ productId, productShopId: product.shop_id, requestedShopId: shopId }));
  }

  let result = Map({
    channel,
    linkId: shapeId(product.link_id),
    productId,
    shopId,
    scheduledAt: shapeUnixDate(product.scheduled_at),
  });

  switch (channel) {
    case EBAY: {
      const {
        category_id: itemCategory,
        custom_category_id: storeCategory,
        condition_id: conditionId,
        condition_description: conditionDescription,
        channel_listing_id: channelProductId,
        country,
        description,
        is_copied: isCopied,
        item_specifics: itemSpecifics = [],
        location,
        merged_products_shop_ids: linkedProductsShopIds,
        payment_policy_id: paymentPolicyId,
        postal_code: postalCode,
        price,
        product_listing_details: productDetails = [],
        quantity,
        return_policy_id: returnPolicyId,
        sku_required: skuRequired,
        shipping_package_irregular: irregular,
        shipping_policy_id: shippingPolicyId,
        sku,
        status,
        title,
        variations,
      } = product;

      const details = Map({
        [ATTRIBUTE.CONDITION]: Map({
          [ATTRIBUTE.DESCRIPTION]: conditionDescription,
          [VALUE]: conditionId,
        }),
        [ATTRIBUTE.ITEM_CATEGORY]: makeValue(itemCategory),
        [ATTRIBUTE.ITEM_SPECIFICS]: updateItemSpecificsForCategory({
          category: itemCategory,
          channel,
          itemSpecifics: makeValue(shapeEbayDetailsForApp(itemSpecifics)),
          taxonomyData,
        }),
        [ATTRIBUTE.PRODUCT_DETAILS]: updateProductDetailsForCategory({
          category: itemCategory,
          channel,
          productDetails: makeValue(shapeEbayDetailsForApp(productDetails)),
          taxonomyData,
        }),
        [ATTRIBUTE.STORE_CATEGORY]: makeValue(storeCategory || null),
      });

      const inventory = Map({
        [ATTRIBUTE.SKU]: Map({
          [REQUIRED]: !isVelaStatus({ channel, status }) && skuRequired,
          [VALUE]: sku || '',
        }),
        [ATTRIBUTE.PRICE]: makeValue(toFixed(price, 2) || ''),
        [ATTRIBUTE.QUANTITY]: makeValue(toString(quantity) || ''),
      });

      const shipping = Map({
        [ATTRIBUTE.COUNTRY]: makeValue(country || null),
        [ATTRIBUTE.DEPTH]: shapeEbayShippingAttributeForApp({ attribute: 'depth', product }),
        [ATTRIBUTE.IRREGULAR]: makeValue(irregular || false),
        [ATTRIBUTE.LENGTH]: shapeEbayShippingAttributeForApp({ attribute: 'length', product }),
        [ATTRIBUTE.LOCATION]: makeValue(location || null),
        [ATTRIBUTE.PAYMENT_POLICY]: makeValue(paymentPolicyId || null),
        [ATTRIBUTE.POSTAL_CODE]: makeValue(postalCode || null),
        [ATTRIBUTE.RETURN_POLICY]: makeValue(returnPolicyId || null),
        [ATTRIBUTE.SHIPPING_POLICY]: makeValue(shippingPolicyId || null),
        [ATTRIBUTE.WEIGHT_MAJOR]: shapeEbayShippingAttributeForApp({ attribute: 'major_weight', product }),
        [ATTRIBUTE.WEIGHT_MINOR]: shapeEbayShippingAttributeForApp({ attribute: 'minor_weight', product }),
        [ATTRIBUTE.WIDTH]: shapeEbayShippingAttributeForApp({ attribute: 'width', product }),
      });

      result = result
        .set('channelProductId', channelProductId)
        .set('status', isCopied ? STATUS[VELA].COPIED : status)
        .set('linkedProductsShopIds', linkedProductsShopIds.map(shapeId))
        .set(FORM.DESCRIPTION, makeValue(description || ''))
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PHOTOS, makeValue(photosFromImagesById({ imagesById, imageIds: product.photos })))
        .set(FORM.SHIPPING, shipping)
        .set(FORM.TITLE, makeValue(title || ''))
        .set(FORM.VIDEOS, makeValue(videosFromProduct({ videos, productId })));

      result = result.set(FORM.VARIATIONS, shapeVariationsForApp({ asProfile, data: variations, product: result }));
      break;
    }

    case ETSY: {
      const {
        tags,
        title,
        files,
        price,
        quantity,
        materials,
        description,
        state: status,
        attributes = [],
        who_made: whoMade,
        is_copied: isCopied,
        is_supply: isSupply,
        when_made: whenMade,
        section_id: section,
        item_width: width,
        item_weight: weight,
        item_height: height,
        item_length: length,
        is_digital: digital,
        productOfferings = [],
        taxonomy_id: taxonomyId,
        listing_id: channelProductId,
        return_policy_id: returnPolicyId,
        variations: productVariations = {},
        should_auto_renew: shouldAutoRenew,
        readiness_state_id: processingProfile,
        shipping_template_id: shippingTemplateId,
        shipping_template_type: shippingTemplateType,
        production_partner_ids: productionPartnerIds,
        merged_products_shop_ids: linkedProductsShopIds,
      } = product;

      const { sku = null } = productOfferings[0] || {};
      const category = shapeId(taxonomyId) || AMOUNT.ZERO;

      const details = Map({
        [ATTRIBUTE.CATEGORY]: makeValue(category),
        [ATTRIBUTE.DIGITAL]: makeValue(!!digital),
        [ATTRIBUTE.IS_SUPPLY]: makeValue(typeof isSupply === 'boolean' ? isSupply : null),
        [ATTRIBUTE.MATERIALS]: makeValue(tagsFromArray(materials)),
        [ATTRIBUTE.PRODUCTION_PARTNERS]: makeValue(List(productionPartnerIds)),
        [ATTRIBUTE.SECTION]: makeValue(section),
        [ATTRIBUTE.SHOULD_AUTO_RENEW]: makeValue(!!shouldAutoRenew),
        [ATTRIBUTE.WHEN_MADE]: makeValue(shapeAboutValue({ attribute: ATTRIBUTE.WHEN_MADE, channel, value: whenMade })),
        [ATTRIBUTE.WHO_MADE]: makeValue(shapeAboutValue({ attribute: ATTRIBUTE.WHO_MADE, channel, value: whoMade })),
        [ATTRIBUTE.TAXONOMY_ATTRIBUTES]: shapeTaxonomyAttributesForApp(attributes),
      });

      const inventory = Map({
        [ATTRIBUTE.SKU]: makeValue(sku || ''),
        [ATTRIBUTE.PRICE]: makeValue(toFixed(price, 2) || ''),
        [ATTRIBUTE.QUANTITY]: makeValue(toString(quantity) || ''),
      });

      if (digital) {
        result = result.set(FORM.FILES, makeValue(shapeFilesForApp(files)));
      } else {
        result = result.set(FORM.SHIPPING,
          Map({
            [ATTRIBUTE.HEIGHT]: makeValue(toString(height) || '').set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
            [ATTRIBUTE.LENGTH]: makeValue(toString(length) || '').set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
            [ATTRIBUTE.PROFILE_ID]: makeValue(toString(shippingTemplateId) || null),
            [ATTRIBUTE.PROFILE_TYPE]: makeValue(toString(shippingTemplateType) || null),
            [ATTRIBUTE.RETURN_POLICY]: makeValue(shapeId(returnPolicyId) || null),
            [ATTRIBUTE.WEIGHT]: makeValue(toString(weight) || ''),
            [ATTRIBUTE.WIDTH]: makeValue(toString(width) || '').set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
          })
        );

        if (userFeatures.get(FEATURE.PROCESSING_PROFILES)) {
          result = result
            .setIn([FORM.SHIPPING, ATTRIBUTE.PROCESSING_PROFILE],makeValue(toString(processingProfile) || null));
        }
      }

      result = result
        .set('status', isCopied ? STATUS[VELA].COPIED : status)
        .set('channelProductId', shapeId(channelProductId))
        .set('linkedProductsShopIds', linkedProductsShopIds.map(shapeId))
        .set(FORM.DESCRIPTION, makeValue(description || ''))
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PERSONALIZATION, shapePersonalizationForApp(product))
        .set(FORM.PHOTOS, makeValue(photosFromImagesById({ imagesById, imageIds: product.photos })))
        .set(FORM.TAGS, makeValue(tagsFromArray(tags)))
        .set(FORM.TITLE, makeValue(title || ''))
        .set(FORM.VIDEOS, makeValue(videosFromProduct({ videos, productId })));

      result = result.set(FORM.VARIATIONS,
        shapeVariationsForApp({
          asProfile,
          data: {
            taxonomyId: category,
            variations: digital ? {} : productVariations,
            offerings: digital ? [] : productOfferings,
          },
          imagesById,
          product: result,
        })
      );

      break;
    }

    case FAIRE: {
      const {
        allow_sales_when_out_of_stock: continueSelling,
        available_quantity: quantity,
        backordered_until: restockDate,
        description,
        category_id: category,
        channel_listing_id: channelProductId,
        gtin,
        is_copied: isCopied,
        merged_products_shop_ids: linkedProductsShopIds,
        preorderable,
        preorder_details_expected_ship_date: startDate,
        preorder_details_expected_ship_window_end_date: endDate,
        preorder_details_keep_active_past_order_by_date: keepActive,
        preorder_details_stop_selling_at: deadlineDate,
        sku,
        tariff_code: tariffCode,
        title,
        variations,
      } = product;

      const status = isCopied ? STATUS[VELA].COPIED : product.status;
      const details = Map({
        [ATTRIBUTE.CATEGORY]: makeValue(category || null),
      });

      let inventory = Map({
        [ATTRIBUTE.CONTINUE_SELLING]: makeValue(continueSelling || false),
        [ATTRIBUTE.DEADLINE_DATE]: makeValue(shapeKeyDate(deadlineDate)),
        [ATTRIBUTE.DISTANCE_UNIT]: makeValue(shapeFaireDistanceUnitForApp(get('measurements_distance_unit')(product))),
        [ATTRIBUTE.END_DATE]: makeValue(shapeKeyDate(endDate)),
        [ATTRIBUTE.GTIN]: makeValue(gtin || ''),
        [ATTRIBUTE.HEIGHT]: makeValue(shapeFaireShippingAttributeForApp({ attribute: 'height', product })),
        [ATTRIBUTE.INTERNATIONAL_PRICING]: makeValue(isPricingInternational(product)),
        [ATTRIBUTE.KEEP_ACTIVE]: makeValue(keepActive || false),
        [ATTRIBUTE.LENGTH]: makeValue(shapeFaireShippingAttributeForApp({ attribute: 'length', product })),
        [ATTRIBUTE.MASS_UNIT]: makeValue(shapeFaireMassUnitForApp(get('measurements_mass_unit')(product))),
        [ATTRIBUTE.PREORDER]: makeValue(preorderable || false),
        [ATTRIBUTE.QUANTITY]: makeValue(toString(quantity)),
        [ATTRIBUTE.RESTOCK_DATE]: makeValue(restockDate ? new Date(restockDate) : null),
        [ATTRIBUTE.RETAIL_PRICE]: makeValue(shapeFairePricesForApp({ attribute: ATTRIBUTE.RETAIL_PRICE, product })),
        [ATTRIBUTE.SKU]: makeValue(sku || ''),
        [ATTRIBUTE.START_DATE]: makeValue(shapeKeyDate(startDate)),
        [ATTRIBUTE.TARIFF_CODE]: makeValue(tariffCode || ''),
        [ATTRIBUTE.WEIGHT]: makeValue(shapeFaireShippingAttributeForApp({ attribute: 'weight', product })),
        [ATTRIBUTE.WHOLESALE_PRICE]: makeValue(
          shapeFairePricesForApp({ attribute: ATTRIBUTE.WHOLESALE_PRICE, product })
        ),
        [ATTRIBUTE.WIDTH]: makeValue(shapeFaireShippingAttributeForApp({ attribute: 'width', product })),
      });

      if (status === STATUS[FAIRE].PUBLISHED || status === STATUS[FAIRE].UNPUBLISHED) {
        inventory = inventory.setIn([ATTRIBUTE.PRICE, REQUIRED], true);
      }

      const shipping = getFaireShipping(product);

      result = result
        .set('status', status)
        .set('channelProductId', shapeId(channelProductId))
        .set('linkedProductsShopIds', linkedProductsShopIds.map(shapeId))
        .set(FORM.DESCRIPTION, makeValue(description || ''))
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PHOTOS, makeValue(photosFromImagesById({ imagesById, imageIds: product.photos })))
        .set(FORM.SHIPPING, shipping)
        .set(FORM.TITLE, makeValue(title || ''));

      result = result.set(FORM.VARIATIONS, shapeVariationsForApp({ asProfile, data: variations, product: result }));
      break;
    }

    case SHOPIFY: {
      const {
        tags,
        title,
        category,
        status,
        vendor,
        variations,
        is_copied: isCopied,
        body_html: description,
        product_type: productType,
        collection_ids: collections,
        product_id: channelProductId,
        merged_products_shop_ids: linkedProductsShopIds,
        seo_title: pageTitle,
        seo_description: metaDescription,
      } = product;

      const details = Map({
        [ATTRIBUTE.CATEGORY]: makeValue(category || null),
        [ATTRIBUTE.VENDOR]: makeValue(vendor),
        [ATTRIBUTE.PRODUCT_TYPE]: makeValue(productType),
        [ATTRIBUTE.COLLECTIONS]: makeValue(List(collections)),
      });

      const seo = Map({
        [ATTRIBUTE.META_DESCRIPTION]: makeValue(metaDescription || ''),
        [ATTRIBUTE.PAGE_TITLE]: makeValue(pageTitle || ''),
      });

      result = result
        .set('channelProductId', shapeId(channelProductId))
        .set('status', isCopied ? STATUS[VELA].COPIED : status)
        .set('linkedProductsShopIds', linkedProductsShopIds.map(shapeId))
        .set(FORM.DESCRIPTION, makeValue(description || ''))
        .set(FORM.DETAILS, details)
        .set(FORM.PHOTOS, makeValue(photosFromImagesById({ imagesById, imageIds: product.photos })))
        .set(FORM.SEO, seo)
        .set(FORM.TAGS, makeValue(tagsFromArray(tags)))
        .set(FORM.TITLE, makeValue(title || ''))
        .set(FORM.VIDEOS, makeValue(videosFromProduct({ videos, productId })));

      if (!metaDescription) {
        const overset = getOversetValue({ attribute: ATTRIBUTE.META_DESCRIPTION, product: result });
        result = result.setIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION], makeValue(overset).set(OVERSET, true));
      } else {
        const overset = getOversetValue({ attribute: ATTRIBUTE.META_DESCRIPTION, product: result });

        if (metaDescription === overset) {
          result = result.setIn([FORM.SEO, ATTRIBUTE.META_DESCRIPTION, OVERSET], true);
        }
      }

      if (!pageTitle) {
        const overset = getOversetValue({ attribute: ATTRIBUTE.PAGE_TITLE, product: result });
        result = result.setIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE], makeValue(overset).set(OVERSET, true));
      } else {
        const overset = getOversetValue({ attribute: ATTRIBUTE.PAGE_TITLE, product: result });

        if (pageTitle === overset) {
          result = result.setIn([FORM.SEO, ATTRIBUTE.PAGE_TITLE, OVERSET], true);
        }
      }

      result = getInventoryFromVariations({
        asProfile,
        channel,
        imagesById,
        product: result,
        variations,
      });

      break;
    }

    default: {
      break;
    }
  }

  if (userFeatures.get(FEATURE.LISTING_SCORE) && !asProfile) {
    if (channel === SHOPIFY || channel === EBAY) {
      result = result.set('keywords', shapeKeywordsForApp({ channel, value: product.keywords || undefined }));
    }

    result = updateListingScore(result, { titleFormatPreference });
  }

  return setProductErrors({ product: result, shopData, taxonomyData });
}

export function sortShops({ first, prioritized = EMPTY.SET, sorted = EMPTY.LIST } = {}) {
  return function sort(x, y) {
    return x === first
      ? -1
      : y === first
        ? 1
        : prioritized.has(x) === prioritized.has(y)
          ? Math.sign(sorted.indexOf(x) - sorted.indexOf(y))
          : prioritized.has(y) - prioritized.has(x);
  };
}
